import type { Device, DeviceStatus } from '@/types/device';
import { statusConfig } from '@/types/device';
import { downloadCsv, escapeCsvField, escapeCsvImei } from '@/pages/sysadmin/deviceQueueCsv';

export const DEVICE_CSV_HEADERS_SV = [
  'IMEI',
  'ICCID',
  'Status',
  'Organisation',
  'Reg.nr',
  'Märke',
  'Modell',
  'Senaste VIN',
  'SIM-status',
  'Skapad',
  'Uppdaterad',
] as const;

export const DEVICE_CSV_HEADERS_EN = [
  'IMEI',
  'ICCID',
  'Status',
  'Organization',
  'Registration',
  'Make',
  'Model',
  'Last known VIN',
  'SIM status',
  'Created',
  'Updated',
] as const;

function statusLabel(status: DeviceStatus, language: string): string {
  const cfg = statusConfig[status];
  if (!cfg) return status;
  return language === 'sv' ? cfg.label : cfg.labelEn;
}

function csvDate(value: string | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

export function buildDeviceManagementCsv(devices: Device[], language: string): string {
  const headers = language === 'sv' ? DEVICE_CSV_HEADERS_SV : DEVICE_CSV_HEADERS_EN;
  const headerLine = headers.map((h) => escapeCsvField(h)).join(';');
  const dataLines = devices.map((d) =>
    [
      escapeCsvImei(d.imei ?? ''),
      escapeCsvField(d.iccid ?? ''),
      escapeCsvField(statusLabel(d.status, language)),
      escapeCsvField(d.organization_name ?? ''),
      escapeCsvField(d.vehicle_registration ?? ''),
      escapeCsvField(d.vehicle_brand ?? ''),
      escapeCsvField(d.vehicle_model ?? ''),
      escapeCsvField(d.last_known_vin ?? ''),
      escapeCsvField(d.sim_status ?? ''),
      escapeCsvField(csvDate(d.created_at)),
      escapeCsvField(csvDate(d.updated_at)),
    ].join(';'),
  );
  return [headerLine, ...dataLines].join('\r\n');
}

export function deviceManagementCsvFilename(
  statusFilter: DeviceStatus | '',
  language: string,
  now = new Date(),
): string {
  const stamp = now.toISOString().slice(0, 10);
  const statusPart = statusFilter
    ? statusLabel(statusFilter, language).toLowerCase().replace(/\s+/g, '-')
    : language === 'sv'
      ? 'alla'
      : 'all';
  return `enheter-${statusPart}-${stamp}.csv`;
}

export function downloadDeviceManagementCsv(
  devices: Device[],
  statusFilter: DeviceStatus | '',
  language: string,
): void {
  const csv = buildDeviceManagementCsv(devices, language);
  downloadCsv(deviceManagementCsvFilename(statusFilter, language), csv);
}
